<script setup lang="ts">
import { ref, watch, defineEmits, defineProps } from 'vue'
import type {
  WheelsPickableAddress,
  WheelsSearchResult,
  WheelsSearchStrategy,
  WheelsPlaceDetailStrategy,
} from './WheelsAddressPicker.constants'
import { WheelsSvgIcon, WheelsTripMapPoint } from '../../components'

const props = defineProps<{
  address?: WheelsPickableAddress
  searchStrategy: WheelsSearchStrategy
  placeDetailStrategy: WheelsPlaceDetailStrategy
}>()

const emit = defineEmits<{
  (e: 'change', address: WheelsPickableAddress): void
}>()

const loading = ref(false)
const detailLoading = ref(false)
const isDropdownActive = ref(false)
const search = ref('')
const debounce = ref<number | null>(null)
const recommendedAddresses = ref<Array<WheelsSearchResult>>([])
const arrowPosition = ref<number | undefined>(undefined)
const isSelectingAddress = ref(false)

// Match the initial search behavior
watch(
  () => props.address,
  (val) => {
    search.value = val?.name || ''
  },
  { immediate: true },
)

// Watch search value changes to trigger search
watch(
  () => search.value,
  (val) => {
    if (val === props.address?.name || isSelectingAddress.value) {
      return
    }
    addressAutoComplete(val)
  },
)

const addressAutoComplete = (input: string): void => {
  if (!input) {
    clearPlace()
    return
  }
  if (debounce.value) {
    window.clearTimeout(debounce.value)
  }

  debounce.value = window.setTimeout(async () => {
    loading.value = true
    arrowPosition.value = undefined
    try {
      recommendedAddresses.value = await props.searchStrategy(input)
      isDropdownActive.value = true
    } catch (error) {
      console.error('Error fetching autocomplete results:', error)
      recommendedAddresses.value = []
    } finally {
      loading.value = false
    }
  }, 250)
}

const handleKeyEvent = (event: KeyboardEvent): void => {
  const maxPosition = recommendedAddresses.value.length

  if (event.key === 'ArrowUp') {
    if (typeof arrowPosition.value === 'undefined') {
      arrowPosition.value = maxPosition
    } else if (arrowPosition.value >= 0) {
      arrowPosition.value = arrowPosition.value - 1
    }
    return
  }

  if (event.key === 'ArrowDown') {
    if (typeof arrowPosition.value === 'undefined') {
      arrowPosition.value = 0
    } else if (arrowPosition.value + 1 === maxPosition) {
      arrowPosition.value = 0
    } else {
      arrowPosition.value = arrowPosition.value + 1
    }
    return
  }

  if (event.key === 'Escape') {
    isDropdownActive.value = false
    arrowPosition.value = undefined
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    event.stopPropagation()
    if (
      typeof recommendedAddresses.value[arrowPosition.value as number] !==
      'undefined'
    ) {
      handleRecommendedAddressClick(
        recommendedAddresses.value[arrowPosition.value as number],
      )
    }
    return
  }
}

const handleRecommendedAddressClick = async (
  searchResult: WheelsSearchResult,
): Promise<void> => {
  isSelectingAddress.value = true
  detailLoading.value = true

  try {
    // Call placeDetailStrategy to fetch full address details
    const fullAddress = await props.placeDetailStrategy(
      searchResult.id,
      searchResult.source,
    )

    // Update the input display with the search result name
    search.value = searchResult.description || ''

    emit('change', fullAddress)

    isDropdownActive.value = false
  } catch (error) {
    console.error('Error fetching address details:', error)
  } finally {
    detailLoading.value = false
    // Reset the flag after a short delay to allow the next user input
    setTimeout(() => {
      isSelectingAddress.value = false
    }, 100)
  }
}

const clearPlace = (): void => {
  recommendedAddresses.value = []
  search.value = '' // this triggers the watch
  emit('change', {})
}

const onFocus = (): void => {
  isDropdownActive.value = true
  if (search.value) {
    addressAutoComplete(search.value)
  }
}

const onBlur = (): void => {
  setTimeout(() => {
    isDropdownActive.value = false
    recommendedAddresses.value = []
  }, 500)
}

const handleInputClick = (): void => {
  if (recommendedAddresses.value.length > 0) {
    isDropdownActive.value = true
  }
}
</script>

<template>
  <span class="wheels-address-picker">
    <span class="wheels-address-picker__input wheels-form-field">
      <input
        v-model="search"
        type="text"
        required
        name="address"
        autocomplete="off"
        placeholder="Select a specific location for a more accurate quote"
        :disabled="detailLoading"
        @keydown="handleKeyEvent"
        @focus="onFocus"
        @blur="onBlur"
        @click="handleInputClick"
      />
      <button
        class="wheels-address-picker__clear-btn"
        v-if="search && !detailLoading"
        @click="clearPlace"
      >
        <WheelsSvgIcon icon="close" />
      </button>
      <span v-if="loading || detailLoading" class="wheels-loading-spinner">
        <span class=""></span>
      </span>
    </span>
    <ul
      v-if="isDropdownActive && recommendedAddresses.length"
      class="wheels-form-field__dropdown"
    >
      <li
        v-for="(searchResult, index) in recommendedAddresses"
        :key="`${searchResult.id}-${index}`"
        class="wheels-form-field__dropdown-item"
        :class="{
          'wheels-form-field__dropdown-item--selected': arrowPosition === index,
          'wheels-form-field__dropdown-item--loading': detailLoading,
        }"
        :data-address-name="searchResult.description"
        @mouseover="arrowPosition = index"
        @mousedown.prevent="
          (e) => {
            e.stopPropagation()
            handleRecommendedAddressClick(searchResult)
          }
        "
      >
        <WheelsTripMapPoint class="wheels-form-field__dropdown-item__icon" />
        <span>{{ searchResult.description }}</span>
      </li>
    </ul>
  </span>
</template>

<style scoped lang="scss">
.wheels-address-picker {
  position: relative;
  min-height: 44px;
}

.wheels-address-picker__input {
  position: relative;
  width: 100%;
  display: block;
  padding: 0;
  min-height: 44px;
  border: 2px solid var(--cu-gray-100);
  &:focus-within {
    outline: none;
  }
  input {
    min-height: 44px;
    padding: 0 12px;
    font-size: 16px;
  }
}

.wheels-address-picker__clear-btn {
  width: 1.5rem;
  height: 1.5rem;
  position: absolute;
  right: var(--spacing-2);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
}

.wheels-form-field__dropdown-item__description {
  font-size: 0.875rem;
  color: var(--cu-gray-600);
  display: block;
}

.wheels-form-field__dropdown-item--loading {
  opacity: 0.6;
  pointer-events: none;
}
</style>
